import { EditableCell } from "./EditableCell";
import { NavActionCell } from "./NavActionCell";
import {
  useUpdateTimeEntry,
  useSoftDeleteTimeEntry,
  useRestoreTimeEntry,
  useCreateTimeEntry,
} from "@/hooks/use-admin-time-entries";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { BudgetLineRow, TimeEntry } from "@/hooks/use-project-detail";

interface UserOption {
  id: string;
  full_name: string;
}

interface ProjectMeta {
  is_internal?: boolean;
  project_type?: string | null;
}

interface Props {
  projectId: string;
  project?: ProjectMeta;
  timeEntries: TimeEntry[];
  budgetLines: BudgetLineRow[];
  users: UserOption[];
}

export function AdminTimeEntriesTable({ projectId, project, timeEntries, budgetLines, users }: Props) {
  const requiresComment = project?.is_internal === true || project?.project_type === "hourly";
  const updateMut = useUpdateTimeEntry();
  const deleteMut = useSoftDeleteTimeEntry();
  const restoreMut = useRestoreTimeEntry();
  const createMut = useCreateTimeEntry();

  const userOpts = users.map((u) => ({ value: u.id, label: u.full_name }));
  const assignmentOpts = [
    { value: "", label: "— Ingen tildeling —" },
    ...budgetLines
      .flatMap((bl) =>
        (bl.assignments ?? []).map((a) => ({
          value: a.id,
          label: `${a.label ?? bl.description} (${a.user?.full_name ?? "?"})`,
        })),
      )
      .sort((a, b) => a.label.localeCompare(b.label, "no")),
  ];

  // Nyeste timeføring først — admin-timer er typisk nylig korrigeringsarbeid.
  const sortedEntries = [...timeEntries].sort((a, b) =>
    (b.start_date ?? "").localeCompare(a.start_date ?? ""),
  );

  const sum = timeEntries.reduce((s, te) => s + (te.duration_seconds ?? 0) / 3600, 0);

  const handleDelete = async (entry: TimeEntry) => {
    await deleteMut.mutateAsync({ timeEntryId: entry.id, projectId });
    toast("Timeføring slettet", {
      duration: 5000,
      action: {
        label: "Angre",
        onClick: () => restoreMut.mutate({ timeEntryId: entry.id, projectId }),
      },
    });
  };

  const handleCreate = () => {
    const u = userOpts[0];
    if (!u) {
      toast.error("Ingen brukere tilgjengelig");
      return;
    }
    createMut.mutate({
      projectId,
      user_id: u.value,
      start_date: new Date().toISOString(),
      duration_seconds: 0,
      assignment_id: null,
      budget_line_id: null,
    });
  };

  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">
        Timeføringer
      </div>
      <table className="w-full border-collapse text-sm bg-card border border-border">
        <thead className="bg-muted">
          <tr>
            <th className="px-2 py-1 border border-border w-8 text-left">#</th>
            <th className="px-2 py-1 border border-border w-32 text-left">Dato</th>
            <th className="px-2 py-1 border border-border text-left">Person</th>
            <th className="px-2 py-1 border border-border text-left">Knyttet til</th>
            <th className="px-2 py-1 border border-border text-right w-16">Timer</th>
            <th className="px-2 py-1 border border-border text-left">Notat</th>
            <th className="px-2 py-1 border border-border text-left">
              Kommentar{requiresComment && <span className="text-destructive ml-1">*</span>}
            </th>
            <th className="px-2 py-1 border border-border w-8" />
          </tr>
        </thead>
        <tbody>
          {sortedEntries.map((te, idx) => (
            <tr key={te.id} className={cn(!te.assignment_id && "bg-yellow-50")}>
              <td className="px-2 py-1 border border-border text-muted-foreground">{idx + 1}</td>
              <EditableCell
                dataCol="date"
                value={te.start_date?.slice(0, 10) ?? ""}
                type="date"
                onSave={(newDate) =>
                  updateMut.mutateAsync({
                    timeEntryId: te.id,
                    projectId,
                    patch: { start_date: newDate },
                  })
                }
              />
              <EditableCell
                dataCol="user"
                value={te.user_id ?? ""}
                type="select"
                options={userOpts}
                onSave={(newUserId) =>
                  updateMut.mutateAsync({
                    timeEntryId: te.id,
                    projectId,
                    patch: { user_id: newUserId },
                  })
                }
              />
              <EditableCell
                dataCol="assignment"
                value={te.assignment_id ?? ""}
                type="select"
                options={assignmentOpts}
                onSave={(newAssignmentId) =>
                  updateMut.mutateAsync({
                    timeEntryId: te.id,
                    projectId,
                    patch: { assignment_id: newAssignmentId || null },
                  })
                }
              />
              <EditableCell
                dataCol="hours"
                // 2 desimaler: 1 desimal rundet 0,25t (15 min) opp til 0,3t
                // (18 min) — og lagring av den verdien korrumperte entryen.
                value={Math.round(((te.duration_seconds ?? 0) / 3600) * 100) / 100}
                type="number"
                align="right"
                onSave={(newHours) =>
                  updateMut.mutateAsync({
                    timeEntryId: te.id,
                    projectId,
                    patch: { duration_seconds: Math.round(newHours * 3600) },
                  })
                }
              />
              <EditableCell
                dataCol="notes"
                value={te.task_description ?? ""}
                type="text"
                onSave={(newDesc) =>
                  updateMut.mutateAsync({
                    timeEntryId: te.id,
                    projectId,
                    patch: { task_description: newDesc },
                  })
                }
              />
              <EditableCell
                dataCol="comment"
                value={te.comment ?? ""}
                type="text"
                onSave={(newComment) =>
                  updateMut.mutateAsync({
                    timeEntryId: te.id,
                    projectId,
                    patch: { comment: newComment || null },
                  })
                }
              />
              <NavActionCell
                dataCol="actions"
                onActivate={() => handleDelete(te)}
                className="px-2 py-1 border border-border text-center text-destructive cursor-pointer hover:bg-red-50"
                title="Slett timeføring — Enter aktiverer"
              >
                ×
              </NavActionCell>
            </tr>
          ))}
          <tr className="bg-muted/30 italic text-muted-foreground hover:bg-muted/60">
            <NavActionCell
              dataCol="new-row"
              onActivate={handleCreate}
              colSpan={8}
              className="px-2 py-1 border border-border cursor-pointer"
              title="Opprett ny timeføring — Enter aktiverer"
            >
              + Ny timeføring…
            </NavActionCell>
          </tr>
          <tr className="bg-muted font-semibold">
            <td colSpan={4} className="px-2 py-1 border border-border text-right">Sum:</td>
            <td className="px-2 py-1 border border-border text-right tabular-nums">
              {Math.round(sum * 10) / 10}
            </td>
            <td colSpan={3} className="border border-border" />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
